// src/app/providers/ExternalLinkProvider.tsx
import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useRef,
} from 'react';
import ReactDOM from 'react-dom';

type Ctx = { openExternal: (url: string) => void };
const ExternalLinkCtx = createContext<Ctx>({ openExternal: () => {} });

export const useExternalLink = () => useContext(ExternalLinkCtx);

export const ExternalLinkProvider = ({ children }: { children: ReactNode }) => {
  const [url, setUrl] = useState<string | null>(null);
  const fallbackDone = useRef(false);

  const openExternal = (target: string) => {
    fallbackDone.current = false;
    setUrl(target);
  };
  const close = () => setUrl(null);
  React.useEffect(() => {
    // type-cast because TS doesn’t know about the property
    (window as any).AOAopenExternal = openExternal;
    return () => {
      delete (window as any).AOAopenExternal;
    };
  }, [openExternal]);
  /** Fires only when the browser *refuses* to load the iframe
   *  (X-Frame-Options: DENY or CSP frame-ancestors). */
  const handleError = () => {
    if (!url || fallbackDone.current) return;
    fallbackDone.current = true;
    window.open(url, '_blank', 'noopener,noreferrer');
    close();
  };

  return (
    <ExternalLinkCtx.Provider value={{ openExternal }}>
      {children}
      {url &&
        ReactDOM.createPortal(
          <div
            className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/60"
            onClick={close}
          >
            <div
              className="relative w-11/12 max-w-5xl h-[80vh] bg-white rounded-xl shadow-lg"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={close}
                className="absolute top-3 right-4 text-xl font-bold"
              >
                ✕
              </button>

              <iframe
                src={url}
                title="External content"
                className="w-full h-full rounded-b-xl"
                sandbox="allow-same-origin allow-forms allow-scripts allow-popups"
                onError={handleError} /* ← only this */
              />
            </div>
          </div>,
          document.body,
        )}
    </ExternalLinkCtx.Provider>
  );
};
